import { createContext, useCallback, useContext, useEffect, useSyncExternalStore } from "react";
import type { Api } from "../api.ts";
import { TERMS_REQUIRED_TEXT } from "../data/legal.ts";
import { planLimitText, SEATS_IN_USE_TEXT } from "../data/plan.ts";
import { changedSinceText, gateText } from "../data/steps.ts";
import { isDepartment } from "../departments.ts";
import type { Folder, FolderLoad, FolderStore } from "../folders.ts";
import type { Route } from "../route.ts";
import type { Project, Role } from "../types.ts";

export interface AppCtx {
  api: Api;
  ws: string;
  role: Role | undefined;
  folders: FolderStore;
  go: (r: Route) => void;
  openPalette: () => void;
  /** narrow screen: no inspector, the tree is a drawer, a tap opens */
  compact: boolean;
  /** the signed-in user's id, once /me has answered */
  meId?: string | null;
  /** the signed-in user's name, for "you" lines */
  meName?: string | null;
  /** the public docs page that explains the screen in view (docs.ts), opened in a new tab */
  docsHref?: string;
  /** a production was renamed, got a picture, or was deleted (`archived_at` set): the lists follow */
  onProjectChanged?: (p: Project) => void;
}

export const AppContext = createContext<AppCtx | null>(null);

export function useApp(): AppCtx {
  const c = useContext(AppContext);
  if (!c) throw new Error("AppContext missing");
  return c;
}

/** Live folders of a project: loads them once, re-renders on every change, says how loading went. */
export function useFolders(
  store: FolderStore,
  projectId: string,
): { folders: Folder[]; archived: Folder[]; load: FolderLoad; reload: () => void } {
  const folders = useSyncExternalStore(
    store.subscribe,
    () => store.list(projectId),
    () => EMPTY_FOLDERS,
  );
  const archived = useSyncExternalStore(
    store.subscribe,
    () => store.archived(projectId),
    () => EMPTY_FOLDERS,
  );
  const load = useSyncExternalStore(
    store.subscribe,
    () => store.state(projectId),
    () => IDLE,
  );
  const reload = useCallback(() => void store.load(projectId), [store, projectId]);
  useEffect(() => void store.load(projectId), [store, projectId]);
  return { folders, archived, load, reload };
}

const EMPTY_FOLDERS: Folder[] = [];
const IDLE: FolderLoad = { status: "idle" };

/** Folder list only (for the palette and other readers that do not care about load state). */
export const useFolderList = (store: FolderStore, projectId: string): Folder[] =>
  useFolders(store, projectId).folders;

export function useMediaQuery(q: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(q);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia(q).matches,
    () => false,
  );
}

/** A 409 gate_closed in plain words: the departments the finals wait on, or a shot that is in no episode. */
export function gateClosedText(e: unknown): string {
  const waiting = (e as { details?: { waiting_on?: string[] } }).details?.waiting_on ?? [];
  if (waiting.includes("placement")) return "Place the shot in an episode before you render its final.";
  const depts = waiting.filter(isDepartment);
  return gateText(depts.length ? depts : ["animatic"]).reason;
}

/** The words of the link that ends an out-of-credits sentence; `ErrorText` turns them into a link to #/credits. */
export const ADD_CREDITS = "Add credits";

/** A 402 out_of_credits as a sentence ending in "Add credits". Null for any other error. */
export function outOfCreditsText(e: unknown): string | null {
  const code = e && typeof e === "object" && "code" in e ? (e as { code: unknown }).code : null;
  if (code !== "out_of_credits") return null;
  const d = (e as { details?: { needed?: unknown; available?: unknown } }).details;
  const needed = typeof d?.needed === "number" ? d.needed : null;
  const available = typeof d?.available === "number" ? Math.max(0, d.available) : null;
  const n = (v: number) =>
    `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v)} ${v === 1 ? "credit" : "credits"}`;
  if (needed === null || available === null) return `Not enough credits for this. ${ADD_CREDITS}`;
  return `Not enough credits: this needs ${n(needed)} and ${available === 1 ? "1 is" : `${new Intl.NumberFormat("en-US").format(available)} are`} left. ${ADD_CREDITS}`;
}

/** A server that failed (5xx), in one plain line. */
export const SERVER_BUSY_TEXT = "The server is busy. Try again in a moment.";
/** A request that never reached the server, in one plain line. */
export const OFFLINE_TEXT = "Can’t reach the server. Check that you’re online, then try again.";

/**
 * A 5xx or a dropped connection as one plain line; the server's own words stay out of the page (they go to the
 * console for whoever runs it). Null for anything else, which `errText` says as before.
 */
export function serverTroubleText(e: unknown): string | null {
  const status = e && typeof e === "object" && "status" in e ? (e as { status: unknown }).status : null;
  if (typeof status === "number") {
    if (status < 500) return null;
    console.warn("server error", status, e instanceof Error ? e.message : e);
    return SERVER_BUSY_TEXT;
  }
  const msg = e instanceof Error ? e.message : typeof e === "string" ? e : "";
  if (
    e instanceof TypeError ||
    /failed to fetch|networkerror|network request|load failed|fetch failed/i.test(msg)
  )
    return OFFLINE_TEXT;
  return null;
}

/** An error as a sentence, a server failure or a dropped connection in plain words (`serverTroubleText`). */
export const plainErrText = (e: unknown): string => serverTroubleText(e) ?? errText(e);

/** The same error with a plain message, for code that turns an error into text with `errText` itself. */
export function plainError(e: unknown): unknown {
  const t = serverTroubleText(e);
  return t ? new Error(t) : e;
}

/** An error as a sentence. The production-process refusals (F4) are said in set language. */
export const errText = (e: unknown): string => {
  const code = e && typeof e === "object" && "code" in e ? (e as { code: unknown }).code : null;
  if (code === "gate_closed") return gateClosedText(e);
  if (code === "out_of_credits") return outOfCreditsText(e) ?? "Not enough credits for this.";
  if (code === "plan_limit") return planLimitText(e) ?? "This is above what your plan includes.";
  if (code === "seats_in_use") return SEATS_IN_USE_TEXT;
  if (code === "terms_required") return TERMS_REQUIRED_TEXT;
  if (code === "not_a_signer") return "You don't sign off this department in this production.";
  if (code === "already_signed_off") return "This is already signed off.";
  if (code === "step_skipped") return "This production skips that step.";
  if (code === "changed_since") return changedSinceText();
  if (e && typeof e === "object" && "message" in e) return String((e as { message: unknown }).message);
  return String(e);
};

/** An error message in the studio’s own words: what happened and what to do, for an error panel. */
export function explainError(
  message: string,
  fallbackTitle = "Something went wrong",
): { title: string; detail: string } {
  if (
    /failed to fetch|networkerror|network request|load failed|fetch failed|econn|timed? ?out/i.test(message)
  )
    return {
      title: "Can’t reach the studio server",
      detail:
        "The server did not answer. Your work is safe on the server. Check that it is running and that you are online, then try again.",
    };
  if (/\b403\b|forbidden|not allowed|permission/i.test(message))
    return {
      title: "You don’t have access to this",
      detail: "Ask a workspace owner to add you to it, or switch workspace from the top bar.",
    };
  if (/\b404\b|not found|does not exist|archived/i.test(message))
    return {
      title: "This is no longer here",
      detail: "It may have been archived or moved. Go back to all productions to find it.",
    };
  if (/\b5\d\d\b|internal|unavailable|bad gateway/i.test(message))
    return {
      title: "The studio server hit a problem",
      detail:
        "It is not your doing. Try again in a moment; if it keeps happening, tell whoever runs the server.",
    };
  return { title: fallbackTitle, detail: message };
}

/** Is the key event coming from a text field (where letters and arrows belong to the field)? */
export function isTyping(e: { target: EventTarget | null }): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}

export const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const MOD = isMac ? "⌘" : "Ctrl";

export function timeAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "–";
  const s = Math.max(0, (now - Date.parse(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
